import React, { useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link, useLocation } from "react-router-dom";
import ContentPageLayout from "./ContentPageLayout";
import { NAV } from "../Navbar/navbarTheme";

const FAQ_BLUE = "#1e5a9e";
const FAQ_BLUE_LIGHT = "#e8f2fa";
const FAQ_BLUE_BORDER = "#b8d4eb";

const linkSx = {
  color: FAQ_BLUE,
  fontWeight: 600,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

const answerTextSx = {
  color: NAV.ink,
  fontSize: { xs: "0.9375rem", md: "1rem" },
  lineHeight: 1.75,
  mb: 0,
};

const faqItems = [
  {
    question: "What is the minimum order value?",
    answer:
      "Our minimum wholesale order value is £3,000 FCA (Incoterms® 2020) excluding VAT.",
  },
  {
    question: "Do you supply international customers?",
    answer:
      "Yes. We supply wholesale and export customers across the UK, Europe, the Middle East, Africa, Asia, Australia, New Zealand, the USA, and beyond — subject to product availability and destination requirements.",
  },
  {
    question: "What are your typical lead times?",
    answer:
      "We typically process and prepare orders within 1–2 weeks. During busy periods, lead times may extend to 3 weeks. Sourced or special-order items may take longer, depending on supplier confirmation. Our team will confirm expected timelines at the quotation stage and keep you updated throughout the process.",
  },
  {
    question: "How is pricing determined?",
    answer:
      "Our website operates on a quote-based system. Pricing depends on product, quantity, packaging format (case, pallet, container), destination market, and current supply conditions. We aim to offer competitive wholesale pricing across our full range, with confirmed pricing provided at the quotation stage.",
  },
  {
    question: "Do you display public pricing on the website?",
    answer:
      "No. We operate on a quote-based model. Pricing and availability are confirmed by our sales team after enquiry submission, ensuring up-to-date trade terms for every order.",
  },
  {
    question: "What brands do you supply?",
    answer: (
      <>
        We supply a wide range of British and international FMCG brands across
        food, drinks, confectionery, grocery, household, personal care, baby
        care, frozen, pet care, and specialist categories. Many of the products
        we stock are well-known brands found in major UK supermarkets and retail
        stores. A full brand list is available on our{" "}
        <Box component={Link} to="/Brands" sx={linkSx}>
          Brands page
        </Box>
        .
      </>
    ),
  },
  {
    question: "Can you source products not listed on the website?",
    answer:
      "Yes. Through our supplier network across the UK and Europe, we can source many additional products beyond our listed website range. Please contact us with your requirements and our team will confirm availability and pricing.",
  },
  {
    question:
      "Do you supply Halal, Vegan, Gluten Free or other special diet products?",
    answer:
      "Yes. We supply a range of Halal, Vegan, Gluten Free, Organic, and other special diet FMCG products. Please contact us with your specific requirements and we'll confirm what's available for your market.",
  },
  {
    question: "Can I order mixed products?",
    answer:
      "Yes. We support mixed product orders including mixed pallets and consolidated shipments — useful for buyers who want a varied product mix on a single pallet or container.",
  },
  {
    question: "Do you offer full container shipments?",
    answer:
      "Yes. We supply individual cases, mixed pallets, full pallets, truckloads, and full container shipments depending on customer requirements.",
  },
  {
    question: "How will I receive my goods?",
    answer:
      "We primarily supply on an FCA (Free Carrier) basis under Incoterms® 2020, named at our UK premises. You (or your freight forwarder) arrange collection from our warehouse; we load the goods onto your vehicle and complete the UK export clearance and documentation. If you don't have a freight forwarder in place, we can recommend experienced logistics partners we regularly work with and help obtain competitive shipping quotes. Where you'd prefer us to arrange carriage or delivery, we can also supply under other Incoterms® 2020 rules (such as CPT, CIP, DAP, DPU or DDP) by agreement.",
  },
  {
    question: "Do you provide export documentation?",
    answer:
      "Yes. We assist with export-related documentation including commercial invoices, packing lists, Certificates of Origin, EUR1, Bills of Lading, health certificates, T1 transit documents, and other documents required for international shipments.",
  },
  {
    question: "Do I get a VAT refund?",
    answer: (
      <>
        <Box component="p" sx={{ m: 0, mb: 1.5 }}>
          Where UK TO MARS LTD arranges the export shipment and completes the
          required export documentation, qualifying export orders may be
          supplied at zero-rated (0%) VAT, subject to HMRC export requirements
          and conditions.
        </Box>
        <Box component="p" sx={{ m: 0 }}>
          For customer-collected FCA export orders, VAT may initially be
          charged. Once valid export evidence confirming the goods have left the
          UK is received, we can process a VAT refund or apply the amount as
          account credit for future orders.
        </Box>
      </>
    ),
  },
  {
    question: "Do you offer shipment tracking?",
    answer:
      "Yes. We provide live shipment tracking support for selected shipments, depending on the carrier, destination, and route.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept bank transfer only (UK and international). Specific payment terms — including pro forma invoice, deposit requirements, or letter of credit for larger international orders — are confirmed at the quotation or order acceptance stage. Bank charges, exchange fees, and currency conversion costs are the customer's responsibility unless otherwise agreed in writing.",
  },
  {
    question: "How do I place an order?",
    answer:
      "Simply browse products, add items to your quote basket, and submit your enquiry form. Our team will then contact you regarding pricing, availability, and shipping arrangements.",
  },
  {
    question: "Do I need to register before sending an enquiry?",
    answer:
      "No. You can submit enquiries without registering first. A trade account is created during the enquiry process.",
  },
  {
    question: "I'm a first-time customer. What's the process?",
    answer:
      "We'll guide you through every step. After your first enquiry, our team will confirm pricing and availability, set up your trade account, agree payment and shipping arrangements, and prepare your order for collection or dispatch. We're happy to answer any questions along the way.",
  },
  {
    question: "How can I contact your team?",
    answer: (
      <>
        You can contact us by email, WhatsApp, telephone, or through the enquiry
        form on our website. Full contact details are available on the{" "}
        <Box component={Link} to="/ContactUs" sx={linkSx}>
          Contact Us page
        </Box>
        .
      </>
    ),
  },
  {
    question: "How accurate is the product information shown on your website?",
    answer:
      "We work hard to keep all product details — ingredients, allergens, weights, packaging, and images — as accurate as possible. However, manufacturers regularly reformulate products and update packaging without notice, so some details may differ slightly from the actual stock supplied. We always recommend that buyers check the physical product on receipt, especially for ingredients, allergens, and labelling requirements relevant to the destination market. Our team is also happy to confirm any specific product details before you place an order — just contact us.",
  },
];

function FaqAnswer({ answer }) {
  if (typeof answer === "string") {
    return <Typography sx={answerTextSx}>{answer}</Typography>;
  }
  return (
    <Typography component="div" sx={answerTextSx}>
      {answer}
    </Typography>
  );
}

const FAQPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <ContentPageLayout title="Frequently Asked Questions (FAQ)" centerTitle>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
        }}
      >
        {faqItems.map((item) => (
          <Accordion
            key={item.question}
            disableGutters
            elevation={0}
            sx={{
              border: `1px solid ${NAV.line}`,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: NAV.surface,
              boxShadow: "0 2px 8px rgba(15, 40, 71, 0.04)",
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              "&:before": { display: "none" },
              "&.Mui-expanded": {
                margin: 0,
                borderColor: FAQ_BLUE_BORDER,
                boxShadow: "0 6px 20px rgba(30, 90, 158, 0.1)",
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color: FAQ_BLUE,
                    fontSize: 28,
                    transition: "transform 0.2s ease",
                  }}
                />
              }
              sx={{
                minHeight: { xs: 56, md: 60 },
                px: { xs: 2, md: 2.5 },
                bgcolor: NAV.surface,
                transition: "background-color 0.2s ease",
                "&:hover": { bgcolor: NAV.surfaceSubtle },
                "&.Mui-expanded": {
                  minHeight: { xs: 56, md: 60 },
                  bgcolor: FAQ_BLUE_LIGHT,
                  borderBottom: `1px solid ${FAQ_BLUE_BORDER}`,
                },
                "& .MuiAccordionSummary-content": {
                  my: 1.25,
                  "&.Mui-expanded": { my: 1.25 },
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                  color: NAV.navy,
                  lineHeight: 1.4,
                  pr: 1,
                  textAlign: "left",
                }}
              >
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: { xs: 2, md: 2.5 },
                pt: { xs: 1.5, md: 2 },
                pb: { xs: 2, md: 2.5 },
                bgcolor: NAV.surface,
                textAlign: "left",
              }}
            >
              <FaqAnswer answer={item.answer} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </ContentPageLayout>
  );
};

export default FAQPage;
